import type { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import type {
  ProposalNegotiationParty,
  ProposalResolutionState,
} from "work/proposals/negotiation/proposal-collaboration-contract";
import { proposalCollectionItemHasIdentity } from "work/proposals/negotiation/proposal-operation-identity";
import { decodeProposalPointerSegment } from "work/proposals/negotiation/proposal-operation-projector";

/**
 * The shared checkpoint: the one baseline every own-side (blue) line on the active proposal page is
 * measured against. See "Red and blue lining" in EVENT_NEGOTIATION_DESIGN.md.
 *
 * The rule is that blue shows every change of ours the other party currently sees as a red line, so
 * the baseline is the last version of the terms both parties hold:
 *
 *  - before a revision's first submission, the agreed terms it opened over (`agreedBaseline`);
 *  - otherwise the submitted checkpoint, with each of the viewer's party's current resolution targets
 *    set back to its baseline value. Those targets are exactly what the other party sees as red, and
 *    their baselines are what the other party measures from. When the other party published the
 *    current submission there are none, and the baseline is the submission itself.
 *
 * Never the saved party draft. Measuring unsaved edits against the draft while the redline factory
 * measured the draft against the checkpoint made undo on a reviewer's saved change flip the field
 * between two blue values forever (#6886).
 */
export function proposalRedlineBaselineJSON(params: {
  /** The envelope's projection: the party's saved draft when it has one, else the submitted checkpoint. */
  projection: ProposalJSON;
  /** The envelope's `publishedResolutionStates`: our party's current targets, as the other party has them. */
  publishedResolutionStates: readonly ProposalResolutionState[];
  currentSubmissionId: string;
  viewingParty: ProposalNegotiationParty;
  agreedBaseline?: ProposalJSON | null;
}): ProposalJSON {
  if (params.agreedBaseline) return withoutSupersedes(params.agreedBaseline);

  const baseline = withoutSupersedes(submittedCheckpointJSON(params.projection));
  for (const state of params.publishedResolutionStates) {
    if (state.submissionId !== params.currentSubmissionId) continue;
    if (state.incomingParty !== params.viewingParty) continue;
    restoreTargetBaseline(baseline, state);
  }
  return baseline;
}

/**
 * The submitted checkpoint, as the envelope carries it. A canonical checkpoint advances in place and
 * keeps no predecessor, so its projection has no `supersedes` of its own; the server sets one only on
 * a party draft, and sets it to the checkpoint the draft was saved over.
 */
function submittedCheckpointJSON(projection: ProposalJSON): ProposalJSON {
  return projection.supersedes ?? projection;
}

function withoutSupersedes(proposalJSON: ProposalJSON): ProposalJSON {
  const clone = structuredClone(proposalJSON);
  clone.supersedes = undefined;
  return clone;
}

/** Writes a target's baseline back into the checkpoint, at its scalar path or its collection item. */
function restoreTargetBaseline(baseline: ProposalJSON, state: ProposalResolutionState): void {
  const path = pointerSegments(state.path);
  if (!path) return;
  if (!state.itemId) {
    writeValue(baseline, path, state.baselineValueExists, state.baselineValue);
    return;
  }

  const collection = readValue(baseline, path);
  if (!Array.isArray(collection)) return;
  const itemIndex = collection.findIndex((item) => proposalCollectionItemHasIdentity(item, state.itemId!));
  const leaf = collectionLeafSegments(state);

  if (leaf.length === 0) {
    restoreWholeItem({ baseline, path, collection, itemIndex, state });
    return;
  }
  if (itemIndex === -1) return;
  writeValue(collection[itemIndex], leaf, state.baselineValueExists, state.baselineValue);
}

/**
 * Puts a whole item back as the other party holds it: out of the collection when our change added it,
 * back in when our change removed it.
 *
 * A target carries an item's semantic value, not its display details, so the details come from what
 * the checkpoint still has: the item itself when it is still there, or -- for a document our party
 * replaced -- the `replacedDocuments` the server names beside the collection. A removed document
 * with neither keeps only its identity, and the adapter draws no row it cannot name.
 */
function restoreWholeItem(params: {
  baseline: ProposalJSON;
  path: readonly string[];
  collection: unknown[];
  itemIndex: number;
  state: ProposalResolutionState;
}): void {
  const { baseline, path, collection, itemIndex, state } = params;
  const existing = itemIndex === -1 ? undefined : collection[itemIndex];
  if (itemIndex !== -1) collection.splice(itemIndex, 1);
  if (!state.baselineValueExists) return;

  const details = existing ?? replacedDocument(baseline, path, state.itemId!);
  collection.push(
    isRecord(details) && isRecord(state.baselineValue)
      ? { ...structuredClone(details), ...structuredClone(state.baselineValue) }
      : structuredClone(state.baselineValue),
  );
}

function replacedDocument(baseline: ProposalJSON, path: readonly string[], itemId: string): unknown {
  if (path.at(-1) !== "documents") return undefined;
  const replaced = readValue(baseline, [...path.slice(0, -1), "replacedDocuments"]);
  if (!Array.isArray(replaced)) return undefined;
  return replaced.find((item) => proposalCollectionItemHasIdentity(item, itemId));
}

/** The leaf of an item a target names, from its conflict key: `feeSchedule:categories:<id>:feeRate`. */
function collectionLeafSegments(state: ProposalResolutionState): string[] {
  const prefix = `${state.path.slice(1).replaceAll("/", ":")}:${state.itemId}`;
  if (!state.conflictKey.startsWith(`${prefix}:`)) return [];
  return state.conflictKey.slice(prefix.length + 1).split(":").filter(Boolean);
}

function pointerSegments(pointer: string): string[] | undefined {
  if (!pointer.startsWith("/") || pointer === "/") return undefined;
  return pointer.slice(1).split("/").map(decodeProposalPointerSegment);
}

function readValue(value: unknown, segments: readonly string[]): unknown {
  let current = value;
  for (const segment of segments) {
    if (!isRecord(current)) return undefined;
    current = current[segment];
  }
  return current;
}

/**
 * Sets, or removes, the value at `segments`. A masked or absent parent is left alone: the viewer
 * cannot see the field, so its baseline is not theirs to draw.
 */
function writeValue(target: unknown, segments: readonly string[], exists: boolean, value: unknown): void {
  const parent = readValue(target, segments.slice(0, -1));
  if (!isRecord(parent) || parent.isMasked === true) return;
  const property = segments.at(-1)!;
  if (exists) parent[property] = structuredClone(value);
  else delete parent[property];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
